import { Component, Input, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  ProfileVideo,
  VIDEO_STATUS_LABELS,
  formatVideoDuration,
  isPlayableVideo,
} from 'src/app/models/profile-video.model';
import { VideoPlayerDialogComponent } from './video-player-dialog.component';

/**
 * One profile video, rendered so it can never be mistaken for a photo.
 *
 * A Jowa profile usually leads with a video, and the media column of every user
 * table is otherwise a row of identical square thumbnails — so a video gets a
 * gradient ring, a play glyph and its length, and previews itself muted on
 * hover instead of showing a bigger still. Clicking opens the full player with
 * sound (see VideoPlayerDialogComponent).
 *
 * `size`:
 *   'tile'  — 40px, for a table cell. Hover plays a full-frame muted overlay.
 *   'card'  — 200px, for the user detail page. Plays inline with controls.
 */
@Component({
  selector: 'sa-profile-video-tile',
  templateUrl: './profile-video-tile.component.html',
  styleUrls: ['./profile-video-tile.component.scss'],
  standalone: true,
  imports: [OverlayModule, CommonModule, MatDialogModule, MatIconModule],
})
export class ProfileVideoTileComponent implements OnDestroy {
  hovering = false;
  private previewElement: HTMLVideoElement | null = null;
  private previewFrame: number | null = null;

  @ViewChild('preview') set preview(ref: ElementRef<HTMLVideoElement> | undefined) {
    this.stopPreview();
    this.previewElement = ref ? ref.nativeElement : null;
    if (this.previewElement) {
      // The overlay must be attached and visible before Safari starts playback.
      this.previewFrame = requestAnimationFrame(() => {
        this.previewFrame = null;
        this.playPreview();
      });
    }
  }

  @Input() video: ProfileVideo;

  @Input() size: 'tile' | 'card' = 'tile';

  /** Every video on the profile, so the dialog can step through them. */
  @Input() siblings: ProfileVideo[] = [];

  /** Shown in the player's title bar. */
  @Input() title = '';

  constructor(private dialog: MatDialog) {}

  get playable(): boolean {
    return isPlayableVideo(this.video);
  }

  /** MP4 first — every browser plays it, and the admin ships no HLS library. */
  get source(): string {
    if (!this.video) return '';
    return this.video.mp4_url || this.video.hls_url || '';
  }

  get poster(): string {
    if (!this.video) return '';
    return (this.size === 'tile'
      ? this.video.thumb_url || this.video.poster_url
      : this.video.poster_url || this.video.thumb_url) || '';
  }

  get duration(): string {
    return formatVideoDuration(this.video && this.video.duration_ms);
  }

  get statusLabel(): string {
    return this.video ? VIDEO_STATUS_LABELS[this.video.status] || this.video.status : '';
  }

  get dimensions(): string {
    if (!this.video || !this.video.width || !this.video.height) return '';
    return `${this.video.width}×${this.video.height}`;
  }

  /**
   * Start the muted preview only once the pointer lands, so a 30-row page does
   * not fetch thirty videos it will never show.
   */
  onEnter(): void {
    if (this.size !== 'tile') return;
    this.hovering = true;
  }

  playPreview(): void {
    const element = this.previewElement;
    if (!this.hovering || !element || !this.playable) return;
    element.muted = true;
    const played = element.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {});
    }
  }

  onLeave(): void {
    this.hovering = false;
    this.stopPreview();
  }

  private stopPreview(): void {
    if (this.previewFrame !== null) {
      cancelAnimationFrame(this.previewFrame);
      this.previewFrame = null;
    }
    this.previewElement?.pause();
  }

  ngOnDestroy(): void {
    this.onLeave();
  }

  open(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.video) return;
    this.onLeave();

    const videos = this.siblings && this.siblings.length ? this.siblings : [this.video];
    const index = Math.max(videos.findIndex(item => item.video_id === this.video.video_id), 0);

    this.dialog.open(VideoPlayerDialogComponent, {
      data: { videos, index, title: this.title },
      panelClass: 'video-player-dialog-panel',
      maxWidth: '96vw',
      width: '720px',
      autoFocus: false,
    });
  }
}
